import React, { useState } from 'react';
import { requestCode, verifyCode } from '../services/api';

const styles = {
  wrapper: {
    minHeight: '100vh',
    background: '#1a1a2e',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
  },
  card: {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 16,
    padding: '2.5rem',
    width: '100%',
    maxWidth: 400,
  },
  title: {
    textAlign: 'center',
    color: '#F39C12',
    fontSize: '1.8rem',
    fontWeight: 700,
    marginBottom: 4,
  },
  subtitle: {
    textAlign: 'center',
    color: 'rgba(255,255,255,0.5)',
    fontSize: '0.9rem',
    marginBottom: 32,
  },
  label: {
    display: 'block',
    color: 'rgba(255,255,255,0.7)',
    fontSize: '0.85rem',
    marginBottom: 6,
    fontWeight: 500,
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: 8,
    color: '#fff',
    fontSize: '1rem',
    outline: 'none',
    marginBottom: 18,
    boxSizing: 'border-box',
    letterSpacing: 0.5,
  },
  codeInput: {
    width: '100%',
    padding: '14px',
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: 8,
    color: '#fff',
    fontSize: '1.6rem',
    outline: 'none',
    marginBottom: 18,
    boxSizing: 'border-box',
    textAlign: 'center',
    letterSpacing: 8,
    fontFamily: 'monospace',
  },
  button: {
    width: '100%',
    padding: '12px',
    background: '#6C5CE7',
    border: 'none',
    borderRadius: 8,
    color: '#fff',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: 8,
  },
  buttonGhost: {
    width: '100%',
    padding: '10px',
    background: 'transparent',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: 8,
    color: 'rgba(255,255,255,0.7)',
    fontSize: '0.9rem',
    fontWeight: 500,
    cursor: 'pointer',
    marginTop: 10,
  },
  error: {
    color: '#E74C3C',
    textAlign: 'center',
    fontSize: '0.9rem',
    marginBottom: 16,
  },
  info: {
    color: 'rgba(255,255,255,0.6)',
    textAlign: 'center',
    fontSize: '0.85rem',
    marginBottom: 16,
    lineHeight: 1.5,
  },
};

// Two-step phone+code login (v1.10.42 — replaces UN/PW).
// Step 1: phone → backend sends WhatsApp code via main app.
// Step 2: 6-digit code → backend mints our session token.
export default function Login({ onLogin }) {
  const [step, setStep] = useState('phone');     // 'phone' | 'code'
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSendCode = async (e) => {
    if (e) e.preventDefault();
    setError('');
    setInfo('');
    if (!phone || phone.replace(/\D/g, '').length < 9) {
      setError('Enter a valid phone number (e.g. +972501234567 or 0501234567).');
      return;
    }
    setLoading(true);
    try {
      await requestCode(phone);
      setInfo("We sent a code to your WhatsApp. Check the AirTouch sender.");
      setStep('code');
    } catch (err) {
      const msg = err && err.message ? err.message : '';
      if (msg.includes('429')) {
        setError('Too many requests. Please wait a few minutes.');
      } else if (msg.includes('400')) {
        setError('That phone number looks wrong. Check and try again.');
      } else if (msg.includes('503')) {
        setError('Service temporarily unavailable. Try again in a minute.');
      } else {
        setError('Could not send the code. Try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    if (e) e.preventDefault();
    setError('');
    if (!code || code.replace(/\D/g, '').length < 4) {
      setError('Enter the code from WhatsApp.');
      return;
    }
    setLoading(true);
    try {
      await verifyCode(phone, code);
      onLogin();
    } catch (err) {
      const msg = err && err.message ? err.message : '';
      if (msg.includes('401')) {
        setError('Wrong or expired code. Try again, or send a new code.');
      } else if (msg.includes('403')) {
        setError('This account is not allowed on the desktop. Use the mobile app.');
      } else if (msg.includes('429')) {
        setError('Too many tries. Wait a couple of minutes.');
      } else {
        setError('Could not verify. Try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChangeNumber = () => {
    setStep('phone');
    setCode('');
    setError('');
    setInfo('');
  };

  return (
    <div style={styles.wrapper}>
      <div style={styles.card}>
        <div style={styles.title}>AirTouch</div>
        <div style={styles.subtitle}>Fleet Maintenance Dashboard</div>

        {error && <div style={styles.error}>{error}</div>}
        {info && <div style={styles.info}>{info}</div>}

        {step === 'phone' && (
          <form onSubmit={handleSendCode}>
            <label style={styles.label}>Phone number</label>
            <input
              style={styles.input}
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+972501234567"
              autoFocus
              required
            />
            <button
              style={{ ...styles.button, opacity: loading ? 0.7 : 1 }}
              type="submit"
              disabled={loading}
            >
              {loading ? 'Sending…' : 'Send code'}
            </button>
          </form>
        )}

        {step === 'code' && (
          <form onSubmit={handleVerifyCode}>
            <label style={styles.label}>Code from WhatsApp</label>
            <input
              style={styles.codeInput}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              autoFocus
              required
            />
            <button
              style={{ ...styles.button, opacity: loading ? 0.7 : 1 }}
              type="submit"
              disabled={loading}
            >
              {loading ? 'Verifying…' : 'Sign in'}
            </button>
            <button
              style={styles.buttonGhost}
              type="button"
              onClick={handleSendCode}
              disabled={loading}
            >
              Resend code
            </button>
            <button
              style={styles.buttonGhost}
              type="button"
              onClick={handleChangeNumber}
              disabled={loading}
            >
              Change number
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
